import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixSiteListV2Site from './site-list-v2-site-sites.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

export interface Site {
  /**
   * Site ID.
   * @format GUID
   */
  _id?: string;
  /**
   * HTML app ID for the site.
   * @format GUID
   */
  htmlAppId?: string | null;
  /** Site name. Used in the site's URL. */
  name?: string;
  /** Site display name. Used for display purposes, such as in the browser's tab name. */
  displayName?: string;
  /** Site creation date. */
  _createdDate?: Date | null;
  /** Site updated date. */
  _updatedDate?: Date | null;
  /** Site trashed date. */
  trashedDate?: Date | null;
  /** Whether the site is published. */
  published?: boolean;
  /** Whether the site has a Wix Premium (paid) plan. */
  premium?: boolean;
  /** Live site URL. */
  viewUrl?: string;
  /** Editor site URL. */
  editUrl?: string;
  /** Site thumbnail. */
  thumbnail?: string;
  /**
   * Site owner's account ID.
   * @format GUID
   */
  ownerAccountId?: string;
  /**
   * Site contributors' account IDs.
   * @format GUID
   * @minSize 1
   * @maxSize 1000
   */
  contributorAccountIds?: string[];
  /** Site's Wix editor type. */
  editorType?: EditorTypeWithLiterals;
  /** Whether Wix has blocked this site due to legal issues. */
  blocked?: boolean;
  /**
   * Folder ID that contains this site. Empty if site is at root level.
   * @format GUID
   */
  folderId?: string | null;
  /** Namespace of the site, for internal use. */
  namespace?: NamespaceWithLiterals;
  /** Whether the site has a connected domain. */
  domainConnected?: boolean;
}

export enum EditorType {
  UNKNOWN = 'UNKNOWN',
  EDITOR = 'EDITOR',
  ADI = 'ADI',
  EDITORX = 'EDITORX',
  STUDIO = 'STUDIO',
  ODEDITOR = 'ODEDITOR',
  PICASSO = 'PICASSO',
  WIXEL = 'WIXEL',
  STUDIO_TWO = 'STUDIO_TWO',
  EDITORLESS = 'EDITORLESS',
}

/** @enumType */
export type EditorTypeWithLiterals =
  | EditorType
  | 'UNKNOWN'
  | 'EDITOR'
  | 'ADI'
  | 'EDITORX'
  | 'STUDIO'
  | 'ODEDITOR'
  | 'PICASSO'
  | 'WIXEL'
  | 'STUDIO_TWO'
  | 'EDITORLESS';

export enum Namespace {
  UNKNOWN_NAMESPACE = 'UNKNOWN_NAMESPACE',
  /** Default namespace for UGC sites. MetaSites with this namespace will be shown in a user's site list by default. */
  WIX = 'WIX',
  /** ShoutOut stand alone product. These are siteless (no actual Wix site, no HtmlWeb). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  SHOUT_OUT = 'SHOUT_OUT',
  /** MetaSites created by the Albums product, they appear as part of the Albums app. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  ALBUMS = 'ALBUMS',
  /** Part of the WixStores migration flow, a user tries to migrate and gets this site to view and if the user likes it then stores removes this namespace and deletes the old site with the old stores. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  WIX_STORES_TEST_DRIVE = 'WIX_STORES_TEST_DRIVE',
  /** Hotels standalone (siteless). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  HOTELS = 'HOTELS',
  /** Clubs siteless MetaSites, a club without a wix website. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  CLUBS = 'CLUBS',
  /** A partially created ADI website. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  ONBOARDING_DRAFT = 'ONBOARDING_DRAFT',
  /** AppBuilder for AppStudio / shmite (c). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  DEV_SITE = 'DEV_SITE',
  /** LogoMaker websites offered to the user after logo purchase. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  LOGOS = 'LOGOS',
  /** VideoMaker websites offered to the user after video purchase. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  VIDEO_MAKER = 'VIDEO_MAKER',
  /** MetaSites with this namespace will *not* be shown in a user's site list by default. */
  PARTNER_DASHBOARD = 'PARTNER_DASHBOARD',
  /** MetaSites with this namespace will *not* be shown in a user's site list by default. */
  DEV_CENTER_COMPANY = 'DEV_CENTER_COMPANY',
  /**
   * A draft created by HTML editor on open. Upon "first save" it will be moved to be of WIX domain.
   *
   * Meta site with this namespace will *not* be shown in a user's site list by default.
   */
  HTML_DRAFT = 'HTML_DRAFT',
  /**
   * the user-journey for Fitness users who want to start from managing their business instead of designing their website.
   * Will be accessible from Site List and will not have a website app.
   * Once the user attaches a site, the site will become a regular wixsite.
   */
  SITELESS_BUSINESS = 'SITELESS_BUSINESS',
  /** Belongs to "strategic products" company. Supports new product in the creator's economy space. */
  CREATOR_ECONOMY = 'CREATOR_ECONOMY',
  /** It is to be used in the Business First efforts. */
  DASHBOARD_FIRST = 'DASHBOARD_FIRST',
  /** Bookings business flow with no site. */
  ANYWHERE = 'ANYWHERE',
  /** Namespace for Headless Backoffice with no editor */
  HEADLESS = 'HEADLESS',
  /**
   * Namespace for master site that will exist in parent account that will be referenced by subaccounts
   * The site will be used for account level CSM feature for enterprise
   */
  ACCOUNT_MASTER_CMS = 'ACCOUNT_MASTER_CMS',
  /** Rise.ai Siteless account management for Gift Cards and Store Credit. */
  RISE = 'RISE',
  /**
   * As part of the branded app new funnel, users now can create a meta site that will be branded app first.
   * There's a blank site behind the scene but it's blank).
   * The Mobile company will be the owner of this namespace.
   */
  BRANDED_FIRST = 'BRANDED_FIRST',
  /** Nownia.com Siteless account management for Ai Scheduling Assistant. */
  NOWNIA = 'NOWNIA',
  /**
   * UGC Templates are templates that are created by users for personal use and to sale to other users.
   * The Partners company owns this namespace.
   */
  UGC_TEMPLATE = 'UGC_TEMPLATE',
  /** Codux Headless Sites */
  CODUX = 'CODUX',
  /** Bobb - AI Design Creator. */
  MEDIA_DESIGN_CREATOR = 'MEDIA_DESIGN_CREATOR',
  /**
   * Shared Blog Site is a unique single site across Enterprise account,
   * This site will hold all Blog posts related to the Marketing product.
   */
  SHARED_BLOG_ENTERPRISE = 'SHARED_BLOG_ENTERPRISE',
  /** Standalone forms (siteless). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  STANDALONE_FORMS = 'STANDALONE_FORMS',
  /** Standalone events (siteless). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  STANDALONE_EVENTS = 'STANDALONE_EVENTS',
  /** MIMIR - Siteless account for MIMIR Ai Job runner. */
  MIMIR = 'MIMIR',
  /** Wix Twins platform. */
  TWINS = 'TWINS',
  /** Wix Nano. */
  NANO = 'NANO',
  /** Base44 headless sites. */
  BASE44 = 'BASE44',
  /** Wix Channels Sites */
  CHANNELS = 'CHANNELS',
}

/** @enumType */
export type NamespaceWithLiterals =
  | Namespace
  | 'UNKNOWN_NAMESPACE'
  | 'WIX'
  | 'SHOUT_OUT'
  | 'ALBUMS'
  | 'WIX_STORES_TEST_DRIVE'
  | 'HOTELS'
  | 'CLUBS'
  | 'ONBOARDING_DRAFT'
  | 'DEV_SITE'
  | 'LOGOS'
  | 'VIDEO_MAKER'
  | 'PARTNER_DASHBOARD'
  | 'DEV_CENTER_COMPANY'
  | 'HTML_DRAFT'
  | 'SITELESS_BUSINESS'
  | 'CREATOR_ECONOMY'
  | 'DASHBOARD_FIRST'
  | 'ANYWHERE'
  | 'HEADLESS'
  | 'ACCOUNT_MASTER_CMS'
  | 'RISE'
  | 'BRANDED_FIRST'
  | 'NOWNIA'
  | 'UGC_TEMPLATE'
  | 'CODUX'
  | 'MEDIA_DESIGN_CREATOR'
  | 'SHARED_BLOG_ENTERPRISE'
  | 'STANDALONE_FORMS'
  | 'STANDALONE_EVENTS'
  | 'MIMIR'
  | 'TWINS'
  | 'NANO'
  | 'BASE44'
  | 'CHANNELS';

export interface QuerySitesRequest {
  /**
   * Filter and sorting with the following format:
   * `"filter" : { "displayName": "my-site" }`
   */
  query?: QueryV2;
}

export interface QueryV2 extends QueryV2PagingMethodOneOf {
  /**
   * Paging options to limit and offset the number of items.
   * wix.common.Paging paging = 3;
   * Cursor token pointing to a page of results. Used in all calls following the first request.
   */
  cursorPaging?: CursorPaging;
  /**
   * Filter object with the following format:
   * `"filter" : {
   * "displayName": "my-site",
   * "namespace":{"$in":["value1", "value2"]},
   * "folderId": {"$exists": false}
   * }`
   * Example of operators: `$in`, `$hasSome`, `$hasAll`, `$exists`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object with the following format:
   * `[{"fieldName":"displayName","order":"ASC"},{"fieldName":"createdDate","order":"DESC"}]`
   * @maxSize 4
   */
  sort?: Sorting[];
}

/** @oneof */
export interface QueryV2PagingMethodOneOf {
  /**
   * Paging options to limit and offset the number of items.
   * wix.common.Paging paging = 3;
   * Cursor token pointing to a page of results. Used in all calls following the first request.
   */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QuerySitesResponse {
  /** List of retrieved sites. */
  sites?: Site[];
  /**
   * Paging information.
   * @deprecated
   */
  cursorPaging?: CursorPaging;
  /** Meta data about the returned sites */
  metadata?: PagingMetadataV2;
}

export interface PagingMetadataV2 {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursors to navigate through the result pages. Returned if cursor paging is used. */
  cursors?: Cursors;
}

export interface Cursors {
  /** Cursor pointing to next page in the list of results. */
  next?: string | null;
}

export interface CountSitesRequest {
  /**
   * Filter object. See [Query Sites](https://dev.wix.com/docs/rest/account-level-apis/sites/sites/query-sites) for a list of supported filters.
   * Note: the `premium` and `appIds` filters are not supported in this endpoint.
   */
  filter?: Record<string, any> | null;
}

export interface CountSitesResponse {
  /**
   * Number of sites retrieved basd on the filter.
   * Free Wix accounts are limited to max 1,000 sites.
   * Premium accounts can have an unlimited number of sites.
   */
  count?: number;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type QuerySitesApplicationErrors = {
  code?: 'unauthenticated_user';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type CountSitesApplicationErrors = {
  code?: 'unauthenticated_user';
  description?: string;
  data?: Record<string, any>;
};

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface SiteCreatedEnvelope {
  entity: Site;
  metadata: EventMetadata;
}

/**
 * Triggered when a site is created.
 * @permissionScope Read Site Data
 * @permissionScopeId SCOPE.ACC-DC-OS.READ-SITE
 * @permissionScope Read Site Data
 * @permissionScopeId SCOPE.CX-OS.VIEW-SITES
 * @permissionId SITE_LIST.READ
 * @webhook
 * @eventType wix.site_list.v2.site_created
 * @serviceIdentifier com.wixpress.sitelist.api.SiteListServiceV2
 * @slug created
 * @documentationMaturity preview
 */
export declare function onSiteCreated(
  handler: (event: SiteCreatedEnvelope) => void | Promise<void>
): void;

export interface SiteDeletedEnvelope {
  metadata: EventMetadata;
}

/**
 * Triggered when a site is deleted.
 * @permissionScope Read Site Data
 * @permissionScopeId SCOPE.ACC-DC-OS.READ-SITE
 * @permissionScope Read Site Data
 * @permissionScopeId SCOPE.CX-OS.VIEW-SITES
 * @permissionId SITE_LIST.READ
 * @webhook
 * @eventType wix.site_list.v2.site_deleted
 * @serviceIdentifier com.wixpress.sitelist.api.SiteListServiceV2
 * @slug deleted
 * @documentationMaturity preview
 */
export declare function onSiteDeleted(
  handler: (event: SiteDeletedEnvelope) => void | Promise<void>
): void;

export interface SiteUpdatedEnvelope {
  entity: Site;
  metadata: EventMetadata;
}

/**
 * Triggered when a site is updated.
 * @permissionScope Read Site Data
 * @permissionScopeId SCOPE.ACC-DC-OS.READ-SITE
 * @permissionScope Read Site Data
 * @permissionScopeId SCOPE.CX-OS.VIEW-SITES
 * @permissionId SITE_LIST.READ
 * @webhook
 * @eventType wix.site_list.v2.site_updated
 * @serviceIdentifier com.wixpress.sitelist.api.SiteListServiceV2
 * @slug updated
 * @documentationMaturity preview
 */
export declare function onSiteUpdated(
  handler: (event: SiteUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Retrieves a list of up to 100 sites per request, given the provided paging, sorting and filtering.
 *
 * > **Important**: You can only call this method when authenticated as a Wix user or by using an account level API key.
 * @public
 * @permissionId SITE_LIST.READ
 * @applicableIdentity APP
 * @fqn com.wixpress.sitelist.api.SiteListServiceV2.QuerySites
 */
export function querySites(): SitesQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<Site, 'CURSOR', QuerySitesRequest, QuerySitesResponse>({
    func: async (payload: QuerySitesRequest) => {
      const reqOpts = ambassadorWixSiteListV2Site.querySites(payload);

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QuerySitesRequest['query']) => {
      const args = [query, {}] as [QuerySitesRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({ data }: HttpResponse<QuerySitesResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [])
      );

      return {
        items: transformedData?.sites,
        pagingMetadata: transformedData?.metadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface SitesQueryResult extends QueryCursorResult {
  items: Site[];
  query: SitesQueryBuilder;
  next: () => Promise<SitesQueryResult>;
  prev: () => Promise<SitesQueryResult>;
}

export interface SitesQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (
    propertyName:
      | '_id'
      | 'name'
      | 'published'
      | 'premium'
      | 'editorType'
      | 'folderId'
      | 'namespace'
      | 'domainConnected',
    value: any
  ) => SitesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (
    propertyName:
      | '_id'
      | 'name'
      | 'published'
      | 'premium'
      | 'editorType'
      | 'folderId'
      | 'namespace'
      | 'domainConnected',
    value: any
  ) => SitesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ge: (
    propertyName: '_id' | 'name' | 'folderId',
    value: any
  ) => SitesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  gt: (
    propertyName: '_id' | 'name' | 'folderId',
    value: any
  ) => SitesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  le: (
    propertyName: '_id' | 'name' | 'folderId',
    value: any
  ) => SitesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  lt: (
    propertyName: '_id' | 'name' | 'folderId',
    value: any
  ) => SitesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   */
  startsWith: (
    propertyName: '_id' | 'name' | 'folderId',
    value: string
  ) => SitesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasSome: (
    propertyName:
      | '_id'
      | 'name'
      | 'published'
      | 'premium'
      | 'editorType'
      | 'folderId'
      | 'namespace'
      | 'domainConnected',
    value: any[]
  ) => SitesQueryBuilder;
  in: (
    propertyName:
      | '_id'
      | 'name'
      | 'published'
      | 'premium'
      | 'editorType'
      | 'folderId'
      | 'namespace'
      | 'domainConnected',
    value: any
  ) => SitesQueryBuilder;
  exists: (
    propertyName:
      | '_id'
      | 'name'
      | 'published'
      | 'premium'
      | 'editorType'
      | 'folderId'
      | 'namespace'
      | 'domainConnected',
    value: boolean
  ) => SitesQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  ascending: (
    ...propertyNames: Array<
      'displayName' | '_createdDate' | '_updatedDate' | 'trashedDate'
    >
  ) => SitesQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  descending: (
    ...propertyNames: Array<
      'displayName' | '_createdDate' | '_updatedDate' | 'trashedDate'
    >
  ) => SitesQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => SitesQueryBuilder;
  /** @param cursor - A pointer to specific record */
  skipTo: (cursor: string) => SitesQueryBuilder;
  find: () => Promise<SitesQueryResult>;
}

/**
 * @hidden
 * @fqn com.wixpress.sitelist.api.SiteListServiceV2.QuerySites
 * @requiredField query
 */
export async function typedQuerySites(query: SiteQuery): Promise<
  NonNullablePaths<
    QuerySitesResponse,
    | `sites`
    | `sites.${number}._id`
    | `sites.${number}.name`
    | `sites.${number}.displayName`
    | `sites.${number}.published`
    | `sites.${number}.premium`
    | `sites.${number}.viewUrl`
    | `sites.${number}.editUrl`
    | `sites.${number}.thumbnail`
    | `sites.${number}.ownerAccountId`
    | `sites.${number}.editorType`
    | `sites.${number}.blocked`
    | `sites.${number}.namespace`
    | `sites.${number}.domainConnected`,
    4
  > & {
    __applicationErrorsType?: QuerySitesApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts = ambassadorWixSiteListV2Site.querySites(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface SiteQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: [
        '_id',
        'domainConnected',
        'editorType',
        'folderId',
        'name',
        'namespace',
        'premium',
        'published'
      ];
      operators: '*';
      sort: 'NONE';
    },
    {
      fields: ['_createdDate', '_updatedDate', 'displayName', 'trashedDate'];
      operators: [];
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<Site, SiteQuerySpec>;
export type SiteQuery = {
  /** 
  Paging options to limit and offset the number of items.
  wix.common.Paging paging = 3;
  Cursor token pointing to a page of results. Used in all calls following the first request.  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object with the following format:
  `"filter" : {
  "displayName": "my-site",
  "namespace":{"$in":["value1", "value2"]},
  "folderId": {"$exists": false}
  }`
  Example of operators: `$in`, `$hasSome`, `$hasAll`, `$exists`  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object with the following format:
  `[{"fieldName":"displayName","order":"ASC"},{"fieldName":"createdDate","order":"DESC"}]` 
  @maxSize: 4 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<Site, SiteQuerySpec, SiteQuery>(),
  },
};

/**
 * Retrieves the number of sites, given the provided filtering.
 * See [Query Sites](https://dev.wix.com/docs/rest/account-level-apis/sites/sites/query-sites) for a list of supported filters.
 *
 * > **Important**: You can only call this method when authenticated as a Wix user or by using an account level API key.
 * @public
 * @permissionId SITE_LIST.READ
 * @applicableIdentity APP
 * @fqn com.wixpress.sitelist.api.SiteListServiceV2.CountSites
 */
export async function countSites(options?: CountSitesOptions): Promise<
  NonNullablePaths<CountSitesResponse, `count`, 2> & {
    __applicationErrorsType?: CountSitesApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    filter: options?.filter,
  });

  const reqOpts = ambassadorWixSiteListV2Site.countSites(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { filter: '$[0].filter' },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CountSitesOptions {
  /**
   * Filter object. See [Query Sites](https://dev.wix.com/docs/rest/account-level-apis/sites/sites/query-sites) for a list of supported filters.
   * Note: the `premium` and `appIds` filters are not supported in this endpoint.
   */
  filter?: Record<string, any> | null;
}
